<script setup>
import { computed, inject, ref } from "vue"
import { useRouter, useRoute } from "vue-router"
import GlobalSearchComponent from "../GlobalSearch/index.vue"
import SidebarToggleButton from "./SidebarToggleButton.vue"
import AddCardsMenu from "./AddCardsMenu.vue"
import UserMenu from "./UserMenu.vue"
import DevTourToggle from "./DevTourToggle.vue"
import { useGlobalStore } from "@/stores/GlobalStore"
import { useUserStore } from "@/stores/UserStore"
import { useBatchStore } from "@/stores/BatchStore"
import { useSearchStore } from "@/stores/SearchStore"
import { useCreateBatchStore } from "@/stores/CreateBatchStore"
import { useBillingStore } from "@/stores/BillingStore"
import { useConfigStore } from "@/stores/ConfigStore"
import ProgressJobs from "@/components/MainLayout/Header/ProgressJobs.vue"
import NotificationsMenu from "@/views/Notifications/NotificationsMenu.vue"
import AiCredits from "@/components/MainLayout/Header/AiCredits.vue"
import { useNotificationsStore } from "@/stores/NotificationsStore"

const closeSidebar = inject("closeSidebar")
const showSystemNotificationBar = inject("showSystemNotificationBar")
const effectiveBarHeight = inject("effectiveBarHeight", ref(40))

const router = useRouter()
const route = useRoute()
const globalStore = useGlobalStore()
const userStore = useUserStore()
const batchStore = useBatchStore()
const searchStore = useSearchStore()
const createBatchStore = useCreateBatchStore()
const configStore = useConfigStore()
const notificationsStore = useNotificationsStore()
const billingStore = useBillingStore()

const user = computed(() => userStore.user)
const loggedIn = computed(() => userStore.loggedIn)
const sidebar = computed(() => globalStore.sidebar)

const showAdminBanner = computed(() => globalStore.showAdminBanner)

notificationsStore.fetchUnreadNotificationsCount()

const toggleSidebar = () => {
  globalStore.sidebar = !globalStore.sidebar
  localStorage.setItem("sidebar", JSON.stringify(globalStore.sidebar))
}

const handleOpenSearch = () => {
  searchStore.triggerOpenSearchResult = true
}

const handleUpgrade = () => {
  billingStore.upgradeWindow = true
}

const signOut = () => {
  userStore.logOut()
  closeSidebar()
}

const cdpBatchesSearch = () => {
  if (batchStore.selectedBatch?.type) {
    batchStore.batchKind = batchStore.selectedBatch.type
    searchStore.searchUserBatches({ kind: batchStore.selectedBatch.type })
  } else {
    searchStore.searchUserBatches()
  }

  searchStore.updateSearchStateBasedOnDataObject({
    cdp_search: {
      ...searchStore.cdp_search,
      open: true,
      state: "batches",
      locationOfSearchCall: "add-button",
    },
    searchState: "result",
    selectedFilter: "Batches",
  })
}

const handleCreateNewBatch = () => {
  batchStore.selectedBatchId = null
  createBatchStore.clearCreatingBatchInfo()
  router.push("/create-batch/general-settings")
}

/**
 * The batch the user currently has OPEN, or null.
 *
 * Two surfaces show a batch: the classic batch page (`BatchDetailsFlow`) and
 * the new design's batch-in-inventory (`/inventory/batch/:batchId`) — the same
 * batch wearing Inventory chrome. Only the first was recognised, so opening a
 * batch inside Inventory left "Add Cards" with no idea it was in a batch and
 * the "Add to this Batch" row never rendered.
 */
const openBatchId = computed(() => {
  const inBatch = route.matched?.some((r) => r.name === "BatchDetailsFlow")
    || route.path.startsWith("/inventory/batch/")

  return inBatch ? route.params.batchId || null : null
})

/**
 * "Add to this Batch" is offered only for batches you can still add to.
 *
 * An IMPORT batch is a finished result set the backend produced — there is no
 * scan or upload still to come, and its card count is what the import found.
 * The id comparison matters: `selectedBatch` lags the route for a moment while
 * a different batch loads, and without it the row would flicker off on a
 * perfectly normal batch.
 */
const isImportBatchOpen = computed(() =>
  batchStore.isImportBatch && String(batchStore.selectedBatch?.id) === String(openBatchId.value))

const isBatchDetailsFlow = computed(() => !!openBatchId.value && !isImportBatchOpen.value)

// One add-cards flow serves both surfaces; it is keyed by batch id, so the
// inventory-side batch routes into exactly the same place.
const addToCurrentBatch = () => {
  if (openBatchId.value) {
    router.push(`/batches/${openBatchId.value}/add/types`)
  }
}
</script>

<template>
  <header
    data-cdp-global-header
    class="fixed left-0 w-full z-[1000] bg-tint-1"
    :style="showAdminBanner || showSystemNotificationBar ? { top: `${effectiveBarHeight}px` } : { top: '0px' }"
  >
    <div class="flex items-center justify-between h-12 py-1.5 px-4 md:px-[18px]">
      <div class="flex items-center gap-x-6">
        <div class="flex items-center gap-x-2 md:gap-x-6">
          <SidebarToggleButton :sidebar="sidebar" @toggleSidebar="toggleSidebar" />

          <AddCardsMenu
            v-if="userStore.plan"
            :isBatchDetailsFlow="isBatchDetailsFlow"
            @addToCurrentBatch="addToCurrentBatch"
            @createNewBatch="handleCreateNewBatch"
            @cdpBatchesSearch="cdpBatchesSearch"
            @closeSidebar="closeSidebar"
          />
        </div>
        
      </div>

      <GlobalSearchComponent v-if="userStore.plan" />

      <div class="flex items-center gap-x-4">
        <!-- <ProgressJobs v-if="notificationsStore.inprogressJobsLength && !userStore.flags?.notifications_enabled" /> -->

        <button
          class="md:hidden text-white"
          @click="handleOpenSearch"
          aria-label="Handle Open Search"
        >
          <Icon icon="heroicons:magnifying-glass-20-solid" class="text-2xl" />
        </button>

        <!-- localhost only; renders nothing anywhere else -->
        <DevTourToggle />

        <AiCredits class="hidden lg:block" />

        <UserMenu
          v-if="loggedIn && user"
          class="order-2 md:order-1"
          :user="user"
          @signOut="signOut"
          @closeSidebar="closeSidebar"
          @upgrade="handleUpgrade"
        />

        <NotificationsMenu class="order-1 md:order-2" />
      </div>
    </div>

    <!-- Mobile/tablet AiCredits bar -->
    <Transition name="credits-collapse">
      <div v-show="!globalStore.creditsBarHidden" class="lg:hidden">
        <AiCredits :bar="true" />
      </div>
    </Transition>
  </header>
</template>

<style>
.credits-collapse-enter-active,
.credits-collapse-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}
.credits-collapse-enter-from,
.credits-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.credits-collapse-enter-to,
.credits-collapse-leave-from {
  max-height: 56px;
  opacity: 1;
}
</style>
